import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Alert from '../../../components/ui/Alert';
import Button from '../../../components/ui/Button';
import Card, { CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import Input from '../../../components/ui/Input';
import { Label } from '../../../components/ui/Label';
import Skeleton from '../../../components/ui/Skeleton';
import { usePageTitle } from '../../../hooks/shared';
import { parseElectionDate } from '../../../lib/date-utils';
import { MAILER_TRIGGERS } from '../../../lib/mailer-triggers';
import { authAPI } from '../../identity/api/auth';
import { useAuthStore } from '../../identity/store/authStore';
import { useMailerOrders } from '../hooks/useMailerOrders';

/**
 * Build the delivery-timeline rows for an order:
 *   [ Order placed today, ...each trigger's expected arrival, Election day ]
 *
 * Arrival dates are estimated as electionDate − leadDays from MAILER_TRIGGERS.
 * Dedupes across recipients so we don't list the same date twice.
 */
function buildTimeline(order) {
  if (!order) return [];

  const electionDate = parseElectionDate(
    order.election?.electionDate || order.election?.electionDay
  );
  if (!electionDate || Number.isNaN(electionDate.getTime())) return [];

  const triggerKeys = new Set();
  for (const r of order.recipients || []) {
    for (const t of r.triggers || []) triggerKeys.add(t);
  }

  const arrivals = [...triggerKeys]
    .map((key) => {
      const def = MAILER_TRIGGERS[key];
      if (!def) return null;
      const arriveAt = new Date(electionDate);
      arriveAt.setDate(arriveAt.getDate() - def.leadDays);
      return { key, label: def.label, date: arriveAt };
    })
    .filter(Boolean)
    .sort((a, b) => a.date - b.date);

  const placedAt = order.paidAt ? new Date(order.paidAt) : new Date(order.createdAt);

  return [
    { kind: 'placed', label: 'Order placed', date: placedAt, done: true },
    ...arrivals.map((a) => ({
      kind: 'mailer',
      label: `Mailer arrives — ${a.label}`,
      date: a.date,
      done: false,
    })),
    {
      kind: 'election',
      label: `🗳️ Election Day — ${order.election?.name || 'your election'}`,
      date: electionDate,
      done: false,
    },
  ];
}

function formatDate(d) {
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Build a "Add to Google Calendar" URL for the election.
 * Uses the standard render?action=TEMPLATE format.
 */
function googleCalendarUrl(order) {
  if (!order?.election) return null;
  const electionDate = parseElectionDate(order.election.electionDate || order.election.electionDay);
  if (!electionDate || Number.isNaN(electionDate.getTime())) return null;
  // Google Calendar wants YYYYMMDD/YYYYMMDD for all-day events
  const ymd = (d) =>
    `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  const next = new Date(electionDate);
  next.setDate(next.getDate() + 1);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: order.election.name || 'Election Day',
    dates: `${ymd(electionDate)}/${ymd(next)}`,
    details:
      `Your VotePlan mailer order #${order.id?.slice(0, 8) || ''} covers this election.\n` +
      `Bring your ballot guide and a photo ID.`,
  });
  return `https://calendar.google.com/calendar/render?${params}`;
}

function UpgradeForm({ onSuccess }) {
  const [form, setForm] = useState({ email: '', password: '', firstName: '', lastName: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await authAPI.upgradeAnonymousSession(form);
      localStorage.removeItem('anon_session_token');
      localStorage.setItem('accessToken', result.accessToken);
      onSuccess(result);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 mt-4">
      {error && <Alert variant="destructive">{error}</Alert>}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="firstName">First name</Label>
          <Input
            id="firstName"
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            required
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="lastName">Last name</Label>
          <Input
            id="lastName"
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
            required
            className="mt-1"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          required
          className="mt-1"
        />
      </div>
      <div>
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          name="password"
          type="password"
          value={form.password}
          onChange={handleChange}
          required
          className="mt-1"
        />
      </div>
      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? 'Creating account…' : 'Create account'}
      </Button>
    </form>
  );
}

export default function MailerSuccessPage() {
  usePageTitle('Mailers Confirmed');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const fetchUser = useAuthStore((s) => s.fetchUser);

  const isGuest = !isAuthenticated && Boolean(localStorage.getItem('anon_session_token'));
  const [accountCreated, setAccountCreated] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data: orders, isLoading } = useMailerOrders();
  const [order, setOrder] = useState(null);

  // Pick the most recent paid order — prefer one matching the Stripe session_id
  // from the URL when present (handles cases where multiple orders exist).
  useEffect(() => {
    const rows = orders?.data || [];
    if (!rows.length) return;
    const bySession = sessionId ? rows.find((o) => o.stripeCheckoutSessionId === sessionId) : null;
    const latest = rows[0];
    const match = bySession || latest;
    if (match?.status === 'paid' || match?.status === 'pending') setOrder(match);
  }, [orders, sessionId]);

  const timeline = useMemo(() => buildTimeline(order), [order]);
  const calendarUrl = useMemo(() => googleCalendarUrl(order), [order]);

  const handleAccountCreated = async (result) => {
    setAccessToken(result.accessToken);
    await fetchUser();
    setAccountCreated(true);
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/?ref=${order?.id?.slice(0, 8) || 'voteplan'}`;
    const shareData = {
      title: 'I just ordered a VotePlan mailer',
      text: 'Get a personalized ballot guide mailed to your door before Election Day.',
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // user cancelled the share sheet — no-op
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Skeleton className="h-16 w-full mb-4" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const orderShortId = order?.id ? order.id.slice(0, 8).toUpperCase() : null;
  const recipientCount = order?.recipients?.length || 0;
  const mailerCount = order?.mailerCount || 0;
  const totalCharged = order?.totalCharged ? Number(order.totalCharged).toFixed(2) : '0.00';

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      {/* Header confirmation */}
      <Card className="border-2 border-green-600 bg-green-50">
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 shrink-0 rounded-full bg-green-600 text-white flex items-center justify-center text-xl font-bold">
              ✓
            </div>
            <div>
              <CardTitle className="text-green-800">Your mailers are confirmed!</CardTitle>
              {orderShortId && (
                <p className="text-xs text-green-700 mt-1 font-mono">
                  Order #VP-{orderShortId} · receipt sent to your email
                </p>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {order ? (
            <div className="bg-white p-4 rounded border border-green-200 grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-neutral-500 text-xs uppercase tracking-wide">Election</div>
                <div className="font-semibold">{order.election?.name || '—'}</div>
              </div>
              <div>
                <div className="text-neutral-500 text-xs uppercase tracking-wide">Mailers</div>
                <div className="font-semibold">
                  {mailerCount} ({recipientCount}{' '}
                  {recipientCount === 1 ? 'recipient' : 'recipients'})
                </div>
              </div>
              <div>
                <div className="text-neutral-500 text-xs uppercase tracking-wide">
                  Total charged
                </div>
                <div className="font-semibold">${totalCharged}</div>
              </div>
              <div>
                <div className="text-neutral-500 text-xs uppercase tracking-wide">Status</div>
                <div className="font-semibold capitalize">{order.status}</div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-neutral-700">
              We&apos;re finalizing your order. You&apos;ll get an email receipt shortly.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Delivery timeline */}
      {timeline.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>What happens next</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3">
              {timeline.map((row) => (
                <li
                  key={`${row.kind}-${row.date.toISOString()}`}
                  className="flex items-start gap-3"
                >
                  <span
                    className={`mt-1.5 w-3 h-3 rounded-full shrink-0 ${
                      row.done ? 'bg-green-600' : 'bg-neutral-300'
                    }`}
                    aria-hidden
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{row.label}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">{formatDate(row.date)}</div>
                  </div>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <Card>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-6">
          {calendarUrl && (
            <Button asChild variant="outline">
              <a href={calendarUrl} target="_blank" rel="noopener noreferrer">
                📅 Add Election Day to calendar
              </a>
            </Button>
          )}
          <Button variant="outline" onClick={handleShare}>
            {copied ? '✓ Link copied' : '📤 Share with a neighbor'}
          </Button>
          {!isGuest && (
            <Button onClick={() => navigate('/deliveries')}>View delivery history</Button>
          )}
          <Button variant="outline" onClick={() => navigate('/')}>
            Back to home
          </Button>
        </CardContent>
      </Card>

      {/* Guest account upgrade */}
      {isGuest && !accountCreated && (
        <Card>
          <CardHeader>
            <CardTitle>Save your order — create a free account</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-neutral-600">
              Create an account to track deliveries, update your mailing address, or add more
              recipients in the future. We&apos;ll use the email from your order.
            </p>
            <UpgradeForm onSuccess={handleAccountCreated} />
          </CardContent>
        </Card>
      )}

      {/* Support footer */}
      <p className="text-xs text-neutral-500 text-center">
        Questions? Email{' '}
        <a href="mailto:help@voteplan.app" className="underline hover:text-primary-600">
          help@voteplan.app
        </a>{' '}
        — full refund available within 24 hours of order.
      </p>
    </div>
  );
}
